import { useCallback, useEffect, useState } from 'react';
import { api } from '../../api.js';
import Field from '../../components/Field.jsx';
import Alert from '../../components/ui/Alert.jsx';
import ConfirmModal from '../../components/ui/ConfirmModal.jsx';
import Modal, { ModalActions } from '../../components/ui/Modal.jsx';
import { EmptyState, Loading, PageHeader, Pill, Section } from '../../components/ui/index.jsx';
import { checkDomain, checkRequired } from '../../utils/validate.js';
import { fmtDateTime } from './adminShared.jsx';

function AddDomainModal({ mxHost, onClose, onAdded }) {
  const [form, setForm] = useState({ domain: '', company_name: '', notes: '' });
  const [errors, setErrors] = useState({});
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const set = (k, v) => { setForm({ ...form, [k]: v }); if (errors[k]) setErrors({ ...errors, [k]: undefined }); };

  const save = async () => {
    const found = Object.fromEntries(Object.entries({
      domain: checkDomain(form.domain),
      company_name: checkRequired(form.company_name, 'the company name', { max: 200 }),
    }).filter(([, m]) => m));
    if (Object.keys(found).length) { setErrors(found); return; }
    setBusy(true); setError('');
    try { await api('/admin/honeypot-domains', { method: 'POST', body: form }); onAdded(); } catch (err) {
      setErrors(err.data?.fields || {}); setError(err.data?.fields ? '' : err.message); setBusy(false);
    }
  };

  return (
    <Modal title="Add a honeypot domain" onClose={onClose} onSubmit={save} busy={busy}
      description="A domain bought only for this, that looks like an ordinary company. Planted contacts get addresses at it."
      footer={<ModalActions submitLabel="Add domain" busyLabel="Adding…" busy={busy} onCancel={onClose} />}>
      {error && <Alert tone="danger">{error}</Alert>}
      <Field label="Domain" required value={form.domain} onChange={(e) => set('domain', e.target.value)} error={errors.domain}
        placeholder="brightwell-partners.com" autoCapitalize="none" autoComplete="off" />
      <Field label="Company name" required maxLength={200} value={form.company_name} onChange={(e) => set('company_name', e.target.value)} error={errors.company_name}
        placeholder="Brightwell Partners" hint={!errors.company_name ? 'The made-up company the planted people work for. Make it match the domain.' : undefined} />
      <Field label="Notes" optional as="textarea" rows={2} maxLength={500} counter value={form.notes} onChange={(e) => set('notes', e.target.value)}
        placeholder="Registrar, DNS account, renewal date" />
      <Alert tone="info">
        Before adding it, set its DNS in an account <strong>separate from SummitShield’s</strong>: <span className="mono">MX 10 {mxHost}</span>,
        TXT <span className="mono">v=spf1 -all</span> and <span className="mono">_dmarc</span> TXT <span className="mono">v=DMARC1; p=reject</span>. Use “Check DNS” afterwards.
      </Alert>
    </Modal>
  );
}

function DnsResult({ r }) {
  const row = (ok, label, value) => (
    <li><Pill tone={ok ? 'good' : 'bad'}>{ok ? 'OK' : 'Fix'}</Pill> <strong>{label}</strong> <span className="mono small break">{value || 'missing'}</span></li>
  );
  return (
    <ul className="plain-list small dns-check">
      {row(r.ok.mx, 'MX', r.mx.map((m) => `${m.priority} ${m.host}`).join(', ') + (r.ok.mx ? '' : ` — should be ${r.want_mx}`))}
      {row(r.ok.spf, 'SPF', r.spf + (r.ok.spf ? '' : ' — should be exactly v=spf1 -all'))}
      {row(r.ok.dmarc, 'DMARC', r.dmarc + (r.ok.dmarc ? '' : ' — should include p=reject'))}
    </ul>
  );
}

/** Platform admin: the made-up companies planted contacts belong to. */
export default function AdminHoneypotDomains() {
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [modal, setModal] = useState(null);   // 'add' | { retire: d } | { restore: d }
  const [dns, setDns] = useState({});          // id → result | 'checking' | { error }
  const [notice, setNotice] = useState('');

  const load = useCallback(async () => {
    try { setData(await api('/admin/honeypot-domains')); } catch (err) { setError(err.message); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const checkDns = async (d) => {
    setDns((x) => ({ ...x, [d.id]: 'checking' }));
    try { const r = await api(`/admin/honeypot-domains/${d.id}/dns`); setDns((x) => ({ ...x, [d.id]: r })); } catch (err) { setDns((x) => ({ ...x, [d.id]: { error: err.message } })); }
  };
  const setStatus = async (d, status, text) => {
    await api(`/admin/honeypot-domains/${d.id}`, { method: 'PATCH', body: { status } });
    setModal(null); setNotice(text); await load();
  };

  if (error) return <div className="page"><PageHeader title="Honeypot domains" /><Alert tone="danger">{error}</Alert></div>;
  if (!data) return <div className="page"><Loading /></div>;
  const addButton = <button type="button" className="btn btn-primary" onClick={() => setModal('add')}>Add domain</button>;

  return (
    <div className="page">
      <PageHeader title="Honeypot domains"
        subtitle="Made-up companies whose planted contacts organisers hide in the lists they share. Mail to a planted contact shows the list was used — and by whom."
        actions={data.domains.length > 0 && addButton} />
      {notice && <Alert tone="success">{notice}</Alert>}
      <Alert tone="warn">
        <strong>Pilot:</strong> these domains’ mail goes to <span className="mono">{data.mx_host}</span>, which also takes SummitShield’s check
        addresses. Its greeting, certificate and reverse DNS name SummitShield, so anyone who looks can link the domains to us. Treat them as
        disposable, and move them to a separate mail server (its own IP and neutral name) before relying on them.
      </Alert>
      <Section flush={data.domains.length > 0}>
        {data.domains.length === 0 ? (
          <EmptyState title="No honeypot domains yet" action={addButton}>
            Organisers can’t plant contacts until there’s at least one active domain.
          </EmptyState>
        ) : (
          <div className="table-scroll">
            <table className="table table-stack">
              <thead><tr><th>Domain</th><th>Company</th><th>Contacts</th><th>Emails received</th><th>Status</th><th><span className="sr-only">Actions</span></th></tr></thead>
              <tbody>
                {data.domains.map((d) => (
                  <tr key={d.id}>
                    <td data-label="Domain">
                      <span className="mono">{d.domain}</span>
                      {d.notes && <div className="muted small">{d.notes}</div>}
                      {dns[d.id] === 'checking' && <div className="muted small">Checking DNS…</div>}
                      {dns[d.id]?.error && <div className="field-error">{dns[d.id].error}</div>}
                      {dns[d.id]?.ok && <DnsResult r={dns[d.id]} />}
                    </td>
                    <td data-label="Company">{d.company_name}</td>
                    <td data-label="Contacts">{d.contacts}</td>
                    <td data-label="Emails received">{d.hits}</td>
                    <td data-label="Status">{d.status === 'active' ? <Pill tone="good">Active</Pill> : <Pill>Retired {fmtDateTime(d.retired_at)}</Pill>}</td>
                    <td className="right row-actions">
                      <button type="button" className="btn btn-small btn-secondary" onClick={() => checkDns(d)}>Check DNS</button>
                      {d.status === 'active'
                        ? <button type="button" className="btn btn-small btn-danger-ghost" onClick={() => setModal({ retire: d })}>Retire</button>
                        : <button type="button" className="btn btn-small btn-secondary" onClick={() => setModal({ restore: d })}>Use again</button>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Section>

      {modal === 'add' && <AddDomainModal mxHost={data.mx_host} onClose={() => setModal(null)} onAdded={() => { setModal(null); setNotice('Domain added. New planted contacts can use it now.'); load(); }} />}
      {modal?.retire && (
        <ConfirmModal title={`Retire ${modal.retire.domain}?`} confirmLabel="Retire" danger onClose={() => setModal(null)}
          onConfirm={() => setStatus(modal.retire, 'retired', 'Retired.')}>
          <p>No new contacts will use it, and the mail server stops accepting mail for its {modal.retire.contacts} planted contact{modal.retire.contacts === 1 ? '' : 's'} within a few minutes. Retire a domain once it’s been exposed.</p>
        </ConfirmModal>
      )}
      {modal?.restore && (
        <ConfirmModal title={`Use ${modal.restore.domain} again?`} confirmLabel="Use again" onClose={() => setModal(null)}
          onConfirm={() => setStatus(modal.restore, 'active', 'It’s active again.')}>
          <p>Its planted contacts receive mail again, and new ones can use it.</p>
        </ConfirmModal>
      )}
    </div>
  );
}
